import { useEffect, useMemo, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";

const ease = [0.2, 0.8, 0.2, 1];
const ROTATE_MS = 6000;
const VISIBLE = 3;

// Illustrative alert pool — cycled through to simulate cadence. Not live quotes.
const POOL = [
  { tag: "High alert", region: "Americas", pair: "DXY", text: "US CPI prints 3.1% YoY vs 3.4% expected — USD sells off across majors." },
  { tag: "Central bank", region: "Europe", pair: "EURUSD", text: "ECB holds rates; Lagarde signals a data-dependent path into Q3." },
  { tag: "Macro", region: "Energy", pair: "USOIL", text: "Crude breaks $84 as OPEC+ extends voluntary cuts — risk sentiment firms." },
  { tag: "High alert", region: "Asia", pair: "USDJPY", text: "BoJ intervention warning as USD/JPY tests 160 — volatility spikes." },
  { tag: "Macro", region: "Americas", pair: "US500", text: "US job growth exceeds expectations, boosting consumer confidence and stocks." },
  { tag: "Central bank", region: "UK", pair: "GBPUSD", text: "BoE vote splits 5–4 on a cut; sterling whipsaws on the decision." },
  { tag: "Macro", region: "Asia", pair: "AUDUSD", text: "China manufacturing PMI slips to 49.2, weighing on AUD and base metals." },
  { tag: "High alert", region: "Metals", pair: "XAUUSD", text: "Gold clears $2,350 record as real yields retreat — momentum accelerates." },
  { tag: "Central bank", region: "Americas", pair: "DXY", text: "Fed minutes flag patience on cuts; two-year yields climb 6bp." },
];

const TONE = { "High alert": "text-[var(--neg)]", "Central bank": "text-[var(--cobalt)]", Macro: "text-[var(--ink-3)]" };

const ago = (ms) => {
  const s = Math.max(0, Math.round(ms / 1000));
  if (s < 60) return `${s}s ago`;
  const m = Math.floor(s / 60);
  return m < 60 ? `${m}m ago` : `${Math.floor(m / 60)}h ago`;
};

const seed = () => {
  const now = Date.now();
  return [0, 1, 2].map((i) => ({ ...POOL[i], id: i, at: now - [12_000, 4 * 60_000, 11 * 60_000][i] }));
};

export const LiveAlertFeed = () => {
  const [items, setItems] = useState(seed);
  const [now, setNow] = useState(Date.now);
  const [nextIdx, setNextIdx] = useState(VISIBLE);

  useEffect(() => {
    const tick = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(tick);
  }, []);

  useEffect(() => {
    const rotate = setInterval(() => {
      setItems((prev) => [{ ...POOL[nextIdx % POOL.length], id: Date.now(), at: Date.now() }, ...prev].slice(0, VISIBLE));
      setNextIdx((i) => i + 1);
    }, ROTATE_MS);
    return () => clearInterval(rotate);
  }, [nextIdx]);

  const latest = useMemo(() => Math.max(...items.map((a) => a.at)), [items]);

  return (
    <div className="card relative flex w-full flex-col overflow-hidden" data-testid="telegram-feed">
      <header className="flex items-baseline justify-between gap-4 border-b border-[var(--line)] px-6 sm:px-7 py-4" data-testid="telegram-feed-header">
        <p className="t-h6">Market Intelligence</p>
        <span className="inline-flex items-center gap-2 font-mono text-[10px] tracking-[0.04em] text-[var(--ink-4)]" data-testid="telegram-feed-live">
          <span className="h-1.5 w-1.5 rounded-full bg-[var(--pos)] animate-pulse" />
          <span className="tabular" data-testid="telegram-feed-updated">Updated {ago(now - latest)}</span>
        </span>
      </header>

      <div className="relative flex flex-1 flex-col" data-testid="telegram-feed-list">
        <AnimatePresence initial={false} mode="popLayout">
          {items.map((a) => (
            <motion.article
              key={a.id}
              layout
              initial={{ opacity: 0, y: -18 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: 18 }}
              transition={{ duration: 0.55, ease }}
              className="group relative flex flex-1 flex-col justify-center px-6 sm:px-7 py-5 border-b border-[var(--line)] last:border-0 transition-colors duration-300 hover:bg-[var(--canvas)]"
              data-testid={`alert-row-${a.pair}`}
            >
              {now - a.at < 2500 && (
                <motion.span
                  aria-hidden
                  className="pointer-events-none absolute inset-0 bg-[var(--cobalt-soft)]"
                  initial={{ opacity: 0.9 }}
                  animate={{ opacity: 0 }}
                  transition={{ duration: 2.2, ease: "easeOut" }}
                />
              )}
              <div className="relative flex items-baseline justify-between gap-4">
                <span className="font-mono text-[10px] tracking-[0.04em] text-[var(--ink-4)]">
                  <span className={TONE[a.tag]}>{a.tag}</span>
                  <span className="mx-1.5 text-[var(--line-2)]">·</span>
                  {a.region}
                </span>
                <time className="font-mono text-[10.5px] tabular text-[var(--ink-4)]" data-testid={`alert-time-${a.pair}`}>{ago(now - a.at)}</time>
              </div>
              <p className="relative mt-2 text-[14.5px] font-medium leading-[1.42] tracking-[-0.012em] text-[var(--ink)]">{a.text}</p>
              <p className="relative mt-2 font-mono text-[10px] tracking-[0.04em] text-[var(--ink-3)]">{a.pair}</p>
            </motion.article>
          ))}
        </AnimatePresence>
      </div>

      <p className="border-t border-[var(--line)] px-6 sm:px-7 py-3 font-mono text-[10px] tracking-[0.04em] text-[var(--ink-4)]">Illustrative feed · Not live quotes</p>
    </div>
  );
};
